import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { GripVertical, Eye, EyeOff, Download } from "lucide-react";
import { useUnsavedChanges } from "@/lib/unsavedChanges";
import { useAuth } from "@/lib/auth";

export default function QRLayoutPage() {
  const { canWrite } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [baseline, setBaseline] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => { api.get("/qr-layout").then(r => { setBlocks(r.data); setBaseline(JSON.stringify(r.data)); }); }, []);

  const move = (from, to) => {
    if (to < 0 || to >= blocks.length) return;
    const next = [...blocks];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setBlocks(next);
  };
  const toggle = (i) => {
    const next = [...blocks];
    next[i] = { ...next[i], is_enabled: !next[i].is_enabled };
    setBlocks(next);
  };
  const toggleDownload = (i) => {
    const next = [...blocks];
    next[i] = { ...next[i], allow_download: !next[i].allow_download };
    setBlocks(next);
  };
  const save = async () => {
    await api.put("/qr-layout", { blocks });
    setBaseline(JSON.stringify(blocks));
    toast.success("Configurazione QR salvata");
    return true;
  };

  const dirty = canWrite && baseline !== null && baseline !== JSON.stringify(blocks);
  useUnsavedChanges(dirty, save);

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIdx !== null && dragIdx !== i) move(dragIdx, i);
    setDragIdx(null);
  };

  return (
    <div className="p-4 pt-16 md:p-8 lg:p-12" data-testid="qr-layout-page">
      <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-4 mb-6 md:mb-8">
        <div>
          <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Configurazione</div>
          <h1 className="font-serif text-3xl md:text-4xl">Layout pagina QR</h1>
          {canWrite && <div className="text-sm text-[#707070] mt-1">Trascina per riordinare. Attiva/disattiva i blocchi visibili nel QR pubblico.</div>}
        </div>
        {canWrite && <button onClick={save} className="btn-primary" data-testid="qr-layout-save-btn">Salva</button>}
      </div>
      <div className="luxury-card p-2 max-w-2xl">
        {blocks.map((b, i) => (
          <div key={b.block_key} draggable={canWrite} onDragStart={()=>canWrite && onDragStart(i)} onDragOver={onDragOver} onDrop={()=>canWrite && onDrop(i)}
               className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-[#E5E5E0] last:border-b-0 hover:bg-[#FAF9F6]"
               data-testid={`qr-block-${b.block_key}`}>
            <div className="flex items-center gap-4 min-w-0">
              {canWrite && <GripVertical className="w-4 h-4 text-[#C5B39B]" strokeWidth={1.5} />}
              <span className="text-[10px] tracking-luxury text-[#707070] uppercase w-8">{i+1}</span>
              <span className="text-sm font-medium break-words">{b.label}</span>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3 sm:gap-5">
              {canWrite && ["safety_sheet", "packaging_certification"].includes(b.block_key) && (
                <button onClick={()=>toggleDownload(i)}
                        disabled={!b.is_enabled}
                        className={`flex items-center gap-2 text-xs tracking-luxury uppercase transition-colors ${
                          !b.is_enabled ? "text-[#C5B39B] cursor-not-allowed" :
                          b.allow_download ? "text-[#1A1A1A]" : "text-[#707070] hover:text-[#1A1A1A]"
                        }`}
                        title={b.block_key === "safety_sheet" ? "Abilita il pulsante di download sulla pagina scheda sicurezza" : "Abilita il download per tutte le Certificazioni Packaging"}
                        data-testid={`qr-block-${b.block_key}-download-toggle`}>
                  <Download className="w-4 h-4" strokeWidth={1.5}/>
                  Download {b.allow_download ? "ON" : "OFF"}
                </button>
              )}
              {canWrite ? <button onClick={()=>toggle(i)}
                       className={`flex items-center gap-2 text-xs tracking-luxury uppercase ${b.is_enabled ? "text-[#1A1A1A]" : "text-[#707070]"}`}
                       data-testid={`qr-block-toggle-${b.block_key}`}>
                {b.is_enabled ? <Eye className="w-4 h-4"/> : <EyeOff className="w-4 h-4"/>}
                {b.is_enabled ? "Visibile" : "Nascosto"}
              </button> : <span className="text-xs text-[#707070]" data-testid={`qr-block-status-${b.block_key}`}>{b.is_enabled ? "Visibile" : "Nascosto"}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
